import api from "../../config/api";
import { handleApiError } from "@/utils/ErrorHandler";

const state = {
    instanceDetail: null,
    instanceWorkgroups: [],
    workgroupAuditLogs: [],
    instanceAdmins: [],
    loading: false,
    error: null,
    deleteResponse: null,
};

const mutations = {
    SET_INSTANCE_DETAIL(state, detail) {
        state.instanceDetail = detail;
    },
    SET_INSTANCE_WORKGROUPS(state, workgroups) {
        state.instanceWorkgroups = workgroups;
    },
    SET_WORKGROUP_AUDIT_LOGS(state, logs) {
        state.workgroupAuditLogs = logs;
    },
    SET_INSTANCE_ADMINS(state, admins) {
        state.instanceAdmins = admins;
    },
    SET_LOADING(state, loading) {
        state.loading = loading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_DELETE_RESPONSE(state, response) {
        state.deleteResponse = response;
    },
};

const actions = {
    /**
     * ✅ Get activity instance detail
     * API: POST /managescheduleajax/
     * cmd: "getactivityinstancedetail"
     */
    async getActivityInstanceDetail({ commit }, { locationId, activityinstanceid }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.post("/managescheduleajax/", {
                cmd: "getactivityinstancedetail",
                locationid: locationId,
                activityinstanceid,
            });

            const detail = response.data?.output;
            commit("SET_INSTANCE_DETAIL", detail);
            return detail;
        } catch (error) {
            const message = handleApiError(error, error.message);
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * ✅ Save instance detail
     * API: POST /managescheduleajax
     * cmd: "saveinstancedetail"
     */
    async saveInstanceDetail({ commit }, { 
        locationId, 
        instanceId, 
        startDate, 
        startTime, 
        endDate, 
        endTime, 
        status, 
        autoScheduled, 
        note 
    }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.post("/managescheduleajax", {
                cmd: "saveinstancedetail",
                locationid: locationId,
                instanceid: instanceId,
                startdate: startDate,
                starttime: startTime,
                enddate: endDate,
                endtime: endTime,
                status: status,
                autoscheduled: autoScheduled,
                note: note,
            });

            const result = response.data?.data || response.data?.output || response.data;
            return result;
        } catch (error) {
            const message = handleApiError(error, "Failed to save instance detail");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * ✅ Get workgroups for an instance
     * API: GET /activity-instance-workgroups/instance/{instanceId}
     */
    async getInstanceWorkgroups({ commit }, instanceId) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.get(`/activity-instance-workgroups/instance/${instanceId}`);
            
            const workgroups = response.data?.output || [];
            commit("SET_INSTANCE_WORKGROUPS", workgroups);
            return workgroups;
        } catch (error) {
            const message = handleApiError(error, "Failed to fetch instance workgroups");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * ✅ Get audit logs for a workgroup instance
     * API: POST /managescheduleajax/
     * cmd: "getAIWGauditlogs"
     */
    async getWorkgroupAuditLogs({ commit }, aworkgroupinstanceid) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.post("/managescheduleajax/", {
                cmd: "getAIWGauditlogs",
                aworkgroupinstanceid: aworkgroupinstanceid,
            });

            const logs = response.data?.data?.logs || response.data?.output?.logs || response.data?.logs || [];
            commit("SET_WORKGROUP_AUDIT_LOGS", logs);
            return logs;
        } catch (error) {
            const message = handleApiError(error, "Failed to fetch workgroup audit logs");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * ✅ Delete workgroup Instance
     * API: POST /manageactivityajax/
     * cmd: "deleteworkgroup"
     */
    async deleteWorkgroupInstance({ commit }, { locationId, workgroupId }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.post("/manageactivityajax/", {
                cmd: "deleteworkgroup",
                locationid: locationId,
                workgroupid: workgroupId,
                workgrouptype: "instance",
            });

            const result = response.data?.data || response.data?.output || response.data;
            commit("SET_DELETE_RESPONSE", result);
            return result;
        } catch (error) {
            const message = handleApiError(error, "Failed to delete workgroup");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * ✅ Get instance admins
     * API: POST /manageinstanceajax
     * cmd: "getinstanceadmins"
     */
    async getInstanceAdmins({ commit }, { locationId, instanceId }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.post("/manageinstanceajax", {
                cmd: "getinstanceadmins",
                locationid: locationId,
                instanceid: instanceId,
            });

            const admins = response.data?.data || response.data?.output || response.data || [];
            commit("SET_INSTANCE_ADMINS", admins);
            return admins;
        } catch (error) {
            const message = handleApiError(error, "Failed to fetch instance admins");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * ✅ Save instance admins
     * API: POST /manageinstanceajax
     * cmd: "saveadmins"
     */
    async saveInstanceAdmins({ commit }, { locationId, instanceId, userIdsArray }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.post("/manageinstanceajax", {
                cmd: "saveadmins",
                locationid: locationId,
                instanceid: instanceId,
                useridsarray: userIdsArray,
            });

            const result = response.data?.data || response.data?.output || response.data;
            return result;
        } catch (error) {
            const message = handleApiError(error, "Failed to save instance admins");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },
};

const getters = {
    instanceDetail: (state) => state.instanceDetail,
    instanceWorkgroups: (state) => state.instanceWorkgroups,
    workgroupAuditLogs: (state) => state.workgroupAuditLogs,
    instanceAdmins: (state) => state.instanceAdmins,
    instanceLoading: (state) => state.loading,
    instanceError: (state) => state.error,
    deleteResponse: (state) => state.deleteResponse,
};

export default {
    state,
    mutations,
    actions,
    getters,
};

